import {
    Box,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material"
import { ReactElement, useEffect } from "react"

import PendingUserRow from "./pendingUsersRows"
import useActiveUsers from "../../../services/middleware/hooks/adminPortal/useActiveUsers"
import AppLoadingButton from "../../buttons/appLoadingButton"
import ZeroPendingUsersMsg from "../../fallback/zeroPendingUsersMsg"
import AppTooltip from "../../helper/appTooltip"
import AppSkeleton from "../../loading/skeletons/appSkeleton"
import RevalidatingSpinner from "../../loading/spinners/revalidatingSpinner"
import AppPaper from "../../surfaces/appPaper"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

interface IHeading {
    title: string
    center: boolean
    enableSort: boolean
}

interface IPendingUserProps {
    filter: string | null
    search: string
    isActive: boolean
    onTotalCountChange?: (count: number) => void
}

const headings: IHeading[] = [
    {
        title: "Name",
        center: false,
        enableSort: true,
    },
    {
        title: "E-Mail",
        center: false,
        enableSort: false,
    },
    {
        title: "MFA Status",
        center: true,
        enableSort: false,
    },
    {
        title: "Actions",
        center: true,
        enableSort: false,
    },
]

/**
 * Displays table with all pending users
 * <PendingUserResults />
 */
const PendingUserResults = ({
    filter,
    search,
    isActive,
    onTotalCountChange,
}: IPendingUserProps): ReactElement => {
    const {
        users: pendingUsers,
        totalCount,
        isLoading: isPendingUserLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
        isValidating,
    } = useActiveUsers(false, filter, search, isActive)

    useEffect(() => {
        onTotalCountChange?.(totalCount)
    }, [totalCount, onTotalCountChange])

    /**
     * Returns flag denoting if no search results returned
     * @returns {boolean}
     */
    const emptySearch = (): boolean => {
        if (isPendingUserLoading) {
            return false
        }
        if (Array.isArray(pendingUsers)) {
            return pendingUsers.length === 0
        }
        if (!pendingUsers) {
            return true
        }
        return false
    }

    return emptySearch() ? (
        <AppPaper>
            <ZeroPendingUsersMsg />
        </AppPaper>
    ) : (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <AppTableCell>
                            <RevalidatingSpinner isValidating={isValidating} />
                        </AppTableCell>
                        {headings.map((heading: IHeading) => (
                            <AppTooltip
                                title={heading.title}
                                key={heading.title}
                            >
                                <AppTableCell
                                    align={heading.center ? "center" : "left"}
                                    sx={{
                                        paddingTop: 0,
                                        cursor: heading.enableSort
                                            ? "pointer"
                                            : null,
                                    }}
                                >
                                    <TableSortLabel>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="primary"
                                        >
                                            {heading.title}
                                        </Typography>
                                    </TableSortLabel>
                                </AppTableCell>
                            </AppTooltip>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {isPendingUserLoading
                        ? Array.from(Array(20).keys()).map((v) => (
                              <AppTableContentRow
                                  hover
                                  key={`SearchTableSkeleton${v}`}
                              >
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                              </AppTableContentRow>
                          ))
                        : pendingUsers?.map((user) => (
                              <PendingUserRow
                                  key={user.userName}
                                  user={user}
                                  mutate={mutate}
                              />
                          ))}
                </TableBody>
            </Table>
            {hasMore && (
                <Box display="flex" justifyContent="center" py={2}>
                    <AppLoadingButton
                        variant="outlined"
                        loading={isLoadingMore}
                        onClick={loadMore}
                    >
                        Load More
                    </AppLoadingButton>
                </Box>
            )}
        </TableContainer>
    )
}

PendingUserResults.defaultProps = {
    onTotalCountChange: undefined,
}

export default PendingUserResults
